import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { TransferRequestFilter } from "@/components/common/section-components/filters/TransferRequestFilter";
import { NoData } from "@/components/common/section-components/NoData";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { TransferRequestActions } from "@/components/common/section-components/action/TransferRequestActions";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { AppRoutes } from "@/constants/routes";
import { TransferRequestStatus } from "@/constants/entity-status";
import { usePolling } from "@/hooks/usePolling";
import { useRoles } from "@/hooks/useRoles";
import { FieldValues, useForm } from "react-hook-form";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { formatCurrency } from "@/utils/format-currency";
import { ScheduledExternalTransferHeaderElements } from "./schema/header-elements";
import {
  clearFiltersHandler,
  submitFiltersHandler,
} from "../transfer/schema/handler-functions";
import {
  ExternalTransferListResponse,
  ExternalTransferRequestItem,
} from "../external-transfer/schema/external-transfer.schema";
import { fetchExternalTransferRequestsHandler } from "../external-transfer/schema/handler-functions";

export const ScheduledExternalTransfersPage = () => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { handleSubmit, setValue, getValues, control } = useForm();
  const { fetchPermissions } = useRoles();
  const navigate = useNavigate();

  const [loadingExternalTransfers, setLoadingExternalTransfers] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [page, setPage] = useState(1);
  const [currentFilters, setCurrentFilters] = useState({});
  const [selection, setSelection] = useState<string[]>([]);

  const [externalTransfers, setExternalTransfers] =
    useState<ExternalTransferListResponse>({
      listado: [],
      pendingRequests: false,
      totalPages: 1,
      totalRecords: 10,
    });

  const fetchExternalTransfers = async (filters?: any, showLoad = true) => {
    await fetchExternalTransferRequestsHandler({
      filters,
      showLoad,
      setLoadingExternalTransfers,
      setExternalTransfers,
      navigate,
      fetchPermissions,
      programadas: true,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchExternalTransfers();
    }
  }, [isActive]);

  usePolling({
    callback: () => {
      if (!loadingExternalTransfers) {
        fetchExternalTransfers({ ...currentFilters, "P.Pagina": page }, false);
      }
    },
    delay: 10000,
    isActive: isActive && !loadingExternalTransfers,
  });

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      setOpenFilters,
    });
  };

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    await submitFiltersHandler({
      event,
      fetchTransferRequests: fetchExternalTransfers,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      fetchPermissions,
      navigate,
      scheduled: true,
      dateField: "FechaEjecucion",
      redirectRoute: AppRoutes.SCHEDULED_EXTERNAL_TRANSFER_REQUESTS,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchExternalTransfers({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (id: number) => {
    navigate(
      AppRoutes.SCHEDULED_EXTERNAL_TRANSFER_REQUEST_DETAIL.replace(
        ":id",
        String(id),
      ),
      { state: { page } },
    );
  };

  const getActionElements = (item: ExternalTransferRequestItem) => (
    <TransferRequestActions handleDetail={handleDetail} item={item as any} />
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Transferencias externas programadas"
        loading={loadingExternalTransfers}
      />

      <TransferRequestFilter
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        clearFilters={clearFilters}
        filtersApplied={filtersApplied}
        submitFilters={submitFilters}
        control={control}
        disableFilters={loadingExternalTransfers}
        externalTransfer
        scheduled
      />

      <Flex flexDir="column">
        {!loadingExternalTransfers && !externalTransfers?.listado?.length && <NoData />}
        {!loadingExternalTransfers && !!externalTransfers?.listado?.length && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={false}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                externalTransfers?.listado?.map((el) => ({
                  ...el,
                  fechaEjecucion: el?.fechaEjecucion
                    ? dayjs(el.fechaEjecucion).format("DD/MM/YYYY")
                    : "-",
                  monto: `$${formatCurrency(el?.monto)}`,
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estadoDescripcion as TransferRequestStatus}
                    />
                  ),
                })) ?? []
              }
              headers={ScheduledExternalTransferHeaderElements}
              actionElements={getActionElements}
            />
            {externalTransfers?.totalPages > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={externalTransfers?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
